import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '../components/ui/sheet';
import PayoutConnectCard from '../components/PayoutConnectCard';
import { CURRENCIES, formatMoney } from '../lib/currency';
import {
  Calendar, Users, MapPin, BarChart3, Plus, Eye,
  Clock, Star, ArrowRight, CheckCircle2, Globe,
  PoundSterling, Settings, ChevronDown, Menu, LogOut
} from 'lucide-react';


const statusColors = {
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-emerald-100 text-emerald-700',
  past: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700',
};

const paymentColors = {
  paid: 'bg-emerald-100 text-emerald-700',
  free: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  refunded: 'bg-gray-100 text-gray-600',
};

const EVENT_CATEGORIES = [
  { value: 'career_fair', label: 'Career Fair' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'networking', label: 'Networking' },
  { value: 'conference', label: 'Conference' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'other', label: 'Other' },
];

const defaultForm = {
  title: '', description: '', date: '', time: '', location: '', is_online: false,
  capacity: '', price: '', currency: 'GBP', is_free: false, category: 'career_fair', image_url: '',
};

export default function EventHostDashboard() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const [activeTab, setActiveTab] = useState('overview');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventForm, setEventForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user?.user_type !== 'event_host' && user?.user_type !== 'admin') {
      navigate('/');
      return;
    }
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get('/events?host=me');
      const evts = res.data?.events || res.data || [];
      setEvents(evts);
      if (evts.length > 0 && !selectedEventId) setSelectedEventId(evts[0].id);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...eventForm,
        capacity: parseInt(eventForm.capacity) || null,
        price: eventForm.is_free ? 0 : parseFloat(eventForm.price) || 0,
      };
      const res = await api.post('/events', payload);
      const newEvent = res.data;
      setEvents([newEvent, ...events]);
      toast.success('Event created successfully!');
      setEventForm(defaultForm);
      setActiveTab('events');
    } catch {
      toast.error('Failed to create event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'events', label: 'My Events', icon: Calendar },
    { id: 'attendees', label: 'Attendees', icon: Users },
    { id: 'create', label: 'Create Event', icon: Plus },
  ];

  const upcomingCount = events.filter(e => e.status === 'upcoming').length;
  const pastCount = events.filter(e => e.status === 'past').length;
  const totalRegistrations = events.reduce((sum, e) => sum + (e.registered || 0), 0);

  const stats = [
    { label: 'Total Events', value: events.length, icon: Calendar, color: 'text-orange-600', bg: 'bg-orange-100' },
    { label: 'Total Registrations', value: totalRegistrations.toLocaleString(), icon: Users, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Upcoming Events', value: upcomingCount, icon: Clock, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Past Events', value: pastCount, icon: CheckCircle2, color: 'text-gray-600', bg: 'bg-gray-100' },
  ];

  const selectedAttendees = [];

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-orange-700 min-h-screen sticky top-0 hidden lg:block">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Event Host</p>
                <p className="text-white/60 text-xs truncate max-w-[140px]">{user?.full_name}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
            <div className="mt-8">
              <Button
                onClick={() => setActiveTab('create')}
                className="w-full bg-white text-orange-700 hover:bg-white/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
              <Button variant="ghost" className="w-full mt-2 text-white/70 hover:text-white hover:bg-white/10" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-orange-700 border-0">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-bold text-sm">Event Host</p>
              </div>
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-8">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 pb-4 border-b">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">
              {TABS.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <div className="max-w-5xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Event Host Dashboard</h1>
                <p className="text-muted-foreground mt-1">Manage events, track registrations and connect with attendees</p>
              </div>
              <Button
                onClick={() => setActiveTab('create')}
                className="bg-orange-700 hover:bg-orange-800 hidden md:flex"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <PayoutConnectCard />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {loading ? (
                    Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)
                  ) : (
                    stats.map((s, i) => (
                      <Card key={i}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">{s.label}</p>
                              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                            </div>
                            <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                              <s.icon className={`w-6 h-6 ${s.color}`} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                <Card className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-2">Reach Thousands of Professionals</h3>
                        <p className="text-white/80">
                          Create and promote your events to our network of job seekers, students and employers.
                        </p>
                      </div>
                      <Button
                        onClick={() => setActiveTab('create')}
                        className="bg-white text-orange-600 hover:bg-white/90 shrink-0"
                      >
                        Create an Event
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle>Upcoming Events</CardTitle></CardHeader>
                  <CardContent>
                    {events.filter(e => e.status === 'upcoming').length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-6">No upcoming events.</p>
                    ) : (
                      <div className="space-y-3">
                        {events.filter(e => e.status === 'upcoming').map((e) => (
                          <div key={e.id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <p className="text-sm font-medium">{e.title}</p>
                              <p className="text-xs text-muted-foreground">{e.date} • {e.registered}/{e.capacity} registered</p>
                            </div>
                            <Badge className="bg-blue-100 text-blue-700">Upcoming</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* My Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">My Events ({events.length})</h2>
                  <Button onClick={() => setActiveTab('create')} className="bg-orange-700 hover:bg-orange-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </div>
                {loading ? (
                  <div className="space-y-3">
                    {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}
                  </div>
                ) : events.length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No events yet.</p>
                      <Button onClick={() => setActiveTab('create')} className="bg-orange-700 hover:bg-orange-800">
                        Create Your First Event
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {events.map((event) => (
                      <Card key={event.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold">{event.title}</h3>
                                <Badge className={statusColors[event.status] || 'bg-gray-100 text-gray-700'}>
                                  {event.status}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {event.date} at {event.time}
                                </span>
                                <span className="flex items-center gap-1">
                                  {event.is_online ? <Globe className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                                  {event.location}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {event.registered}/{event.capacity} registered
                                </span>
                                <span className="font-medium text-foreground">
                                  {event.is_free ? 'Free' : formatMoney(event.price, event.currency)}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setSelectedEventId(event.id); setActiveTab('attendees'); }}
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                Attendees
                              </Button>
                              <Link to={`/events/${event.id}/edit`}>
                                <Button variant="outline" size="sm">Edit</Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Attendees Tab */}
            {activeTab === 'attendees' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                  <h2 className="text-xl font-semibold">Attendees</h2>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Select Event:</Label>
                    <select
                      value={selectedEventId}
                      onChange={(e) => setSelectedEventId(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500/20 bg-white"
                    >
                      {events.map((e) => (
                        <option key={e.id} value={e.id}>{e.title}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {events.find(e => e.id === selectedEventId)?.title || 'Selected Event'} — {selectedAttendees.length} registrants
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {selectedAttendees.length === 0 ? (
                      <div className="text-center py-10">
                        <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm">No registrants for this event yet.</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {selectedAttendees.map((att) => (
                          <div key={att.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-sm">
                                {att.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{att.name}</p>
                                <p className="text-xs text-muted-foreground">{att.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground hidden sm:block">{att.registered_at}</span>
                              <Badge className={paymentColors[att.payment_status] || 'bg-gray-100 text-gray-700'}>
                                {att.payment_status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Create Event Tab */}
            {activeTab === 'create' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Create New Event</h2>
                <Card>
                  <CardContent className="p-6">
                    <form onSubmit={handleCreateEvent} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Event Title *</Label>
                        <Input
                          value={eventForm.title}
                          onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                          placeholder="e.g. UK Tech Jobs Fair 2026"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Description</Label>
                        <textarea
                          className="w-full border rounded-md px-3 py-2 text-sm resize-none outline-none focus:ring-2 focus:ring-orange-500/20"
                          rows={4}
                          value={eventForm.description}
                          onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                          placeholder="Tell attendees what to expect..."
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date *</Label>
                          <Input
                            type="date"
                            value={eventForm.date}
                            onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Time</Label>
                          <Input
                            type="time"
                            value={eventForm.time}
                            onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Location</Label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={eventForm.is_online}
                              onChange={(e) => setEventForm({ ...eventForm, is_online: e.target.checked, location: e.target.checked ? 'Online' : '' })}
                              className="rounded"
                            />
                            Online event
                          </label>
                        </div>
                        <Input
                          value={eventForm.location}
                          onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                          placeholder={eventForm.is_online ? 'Online' : 'Venue name, City'}
                          disabled={eventForm.is_online}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Capacity</Label>
                          <Input
                            type="number"
                            value={eventForm.capacity}
                            onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
                            placeholder="Max attendees"
                            min={1}
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Price</Label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={eventForm.is_free}
                                onChange={(e) => setEventForm({ ...eventForm, is_free: e.target.checked, price: e.target.checked ? '0' : '' })}
                                className="rounded"
                              />
                              Free event
                            </label>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <select
                              value={eventForm.currency}
                              onChange={(e) => setEventForm({ ...eventForm, currency: e.target.value })}
                              disabled={eventForm.is_free}
                              className="rounded-md border border-input bg-background px-2 text-sm"
                            >
                              {CURRENCIES.map(c => (
                                <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                              ))}
                            </select>
                            <Input
                              type="number"
                              value={eventForm.price}
                              onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
                              placeholder="0.00"
                              min={0}
                              step="0.01"
                              disabled={eventForm.is_free}
                              className="col-span-2"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Category</Label>
                        <select
                          value={eventForm.category}
                          onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
                        >
                          {EVENT_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label>Event Image URL (optional)</Label>
                        <Input
                          type="url"
                          value={eventForm.image_url}
                          onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Button
                          type="submit"
                          disabled={submitting}
                          className="bg-orange-700 hover:bg-orange-800"
                        >
                          {submitting ? 'Creating...' : 'Create Event'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEventForm(defaultForm)}
                        >
                          Reset
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
